/**
 * Survey service, backed by Firestore.
 * The response lives at users/{uid}/survey/response; remind-later and
 * eligibility state derive from the user profile doc.
 */

import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../../../shared/firebase';

function requireUid() {
  const fbUser = auth.currentUser;
  if (!fbUser) throw new Error('Not signed in');
  return fbUser.uid;
}

/**
 * Check if the user is eligible to see the survey: 2+ weeks since signup,
 * not completed, and past any remind-me-later date.
 * @returns {Promise<{ showSurvey: boolean }>}
 */
export const getEligibility = async () => {
  const uid = requireUid();
  const [userSnap, surveySnap] = await Promise.all([
    getDoc(doc(db, 'users', uid)),
    getDoc(doc(db, 'users', uid, 'survey', 'response')),
  ]);
  if (!userSnap.exists()) return { showSurvey: false };
  if (surveySnap.exists()) return { showSurvey: false };

  const user = userSnap.data();
  const createdAt = user.createdAt ? new Date(user.createdAt) : null;
  if (!createdAt) return { showSurvey: false };

  const twoWeeksAgo = new Date();
  twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
  if (createdAt > twoWeeksAgo) return { showSurvey: false };

  const remindAt = user.surveyRemindMeLaterAt ? new Date(user.surveyRemindMeLaterAt) : null;
  if (remindAt && remindAt > new Date()) return { showSurvey: false };

  return { showSurvey: true };
};

/**
 * Submit the survey
 * @param {Object} data - Survey responses
 */
export const submitSurvey = async (data) => {
  const uid = requireUid();
  const surveyRef = doc(db, 'users', uid, 'survey', 'response');
  const existing = await getDoc(surveyRef);
  if (existing.exists()) {
    throw new Error('Survey already completed');
  }
  const survey = {
    userId: uid,
    timeSaved: data.timeSaved || null,
    qualityOutput: data.qualityOutput != null ? parseInt(data.qualityOutput, 10) : null,
    otherBenefits: data.otherBenefits || null,
    suggestions: data.suggestions || null,
    createdAt: new Date().toISOString(),
  };
  await setDoc(surveyRef, survey);
  return survey;
};

/**
 * Set remind-me-later (show again in 1 week)
 */
export const remindLater = async () => {
  const uid = requireUid();
  const oneWeekFromNow = new Date();
  oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
  const remindAt = oneWeekFromNow.toISOString();
  await updateDoc(doc(db, 'users', uid), { surveyRemindMeLaterAt: remindAt });
  return { success: true, remindAt };
};
